import { useCallback, useContext, useEffect, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import toastr from "toastr";
import AlertModal from "../../../../utils/components/AlertModal";
import Input from "../../../../utils/components/Input";
import Modal from "../../../../utils/components/Modal";
import SelectBox from "../../../../utils/components/SelectBox";
import { useLanguage } from "../../../../utils/hooks/useLanguage";
import { RequestContext } from "../../../../utils/context/RequestContext";
import API from "../../../../utils/API";
import { useProject } from "../../../../utils/hooks/useProject";
import { processLocations, getLocationLabel } from "../../../../utils/helpers/processLocationLabels";

const AddNewFormDependency = ({
  show,
  setShow,
  translate,
  currentDependencySets,
  refreshFormDependencies,
}) => {
  const { project } = useProject();
  const [ siteSets, setSiteSets ] = useState([]);
  const [ locations, setLocations ] = useState([]);
  const [ selectedSiteSet, setSelectedSiteSet ] = useState(null);
  const [ selectedLocations, setSelectedLocations ] = useState([]);
  const [ selectedName, setSelectedName ] = useState("");
  const [ locationLabels, setLocationLabels ] = useState([]);

  const language = useLanguage();

  const { setRequestLoading } = useContext(RequestContext);

  const linearLocations = (location) => {
    let result = [];
    
    if (location.details) {
      result.push({
        id: location.id,
        value: location.id,
        label: getLocationLabel(location.id, locationLabels)
      });
    }

    if (location?.details && location?.details?.length > 0 &&
      !selectedLocations.some((selectedLocation) => selectedLocation.id === location.id)) {
      result.push(
        ...location.details.reduce((acc, location) => 
          [...acc, ...linearLocations(location)], []
      ));
    }

    return result;
  }

  const handleDynamicLocations = useCallback(() => {
    const result = [];
    for(const location of locations) {
      result.push(...linearLocations(location));
    }
    return result;
  }, [locations, selectedLocations])


  const getSiteSets = async () => {
    try {
      const dependencySetIds = currentDependencySets.map((dependencySet) => dependencySet.site.id);
      const { data } = await API.getData(`site_sets?project_id=${project.id}`);

      if (data?.data) {
        const locationSets = data.data
          .filter((item) => !dependencySetIds
          .includes(item.id)).map((item) => ({
            id: item.id,
            value: String(item.id),
            label: item.name,
            level: item.level,
          }));
        
        setSiteSets(locationSets);
      }
    } catch (error) {
      console.log("error:", error);
      toastr.error(error?.response?.data?.message);
    }
      
  }

  const getLocations = async () => {
    try {
      const { data } = await API.getData(`site_set/${selectedSiteSet?.value}/details`);

      const labels = processLocations(data?.data || []);
      setLocationLabels(labels);

      setLocations(data?.data || []);
    } catch (error) {
      console.log("error:", error);
      toastr.error(error?.response?.data?.message);
    }
      
  }

  const failedValidation = (required) => {
    return required.some((field) => {
      if (!field.validation) {
        confirmAlert({
          customUI: ({ onClose }) => (
            <AlertModal
              buttons={[
                {
                  label: translate("okay"),
                  onClick: onClose,
                  classNames: "btn btn-primary",
                },
              ]}
              message={field.message}
            />
          ),
        });
        return true;
      }
      return false;
    });
  };

  const handleAddFormDependency = async (e) => {
    e.preventDefault();


    const payload = {
      name: selectedName,
      site_id: selectedSiteSet?.value,
      exception_locations: selectedLocations.map((location) => location.value) || [],
    };

    const requiredFields = [
      {
        validation: payload.name && payload.name.length > 0,
        message: translate("project_settings.form_dependencies.add_form_dependency_modal.name_required"),
      },
      {
        validation: payload.site_id,
        message: translate("project_settings.form_dependencies.add_form_dependency_modal.site_set_required"),
      },
      {
        validation: payload.exception_locations && payload.exception_locations.length >= 0 && payload.exception_locations.every((location) => Number.isInteger(location)),
        message: translate("project_settings.form_dependencies.add_form_dependency_modal.exception_locations_invalid"),
      }
    ];

    if (failedValidation(requiredFields)) {
      return;
    }

    setRequestLoading(true);
    try {
      const { data: res } = await API.postData(`form_dependencies?project_id=${project.id}`, payload);
      toastr.success(res?.message);
      refreshFormDependencies();
      setShow(false);
    } catch (error) {
      console.log("error:", error);
      // Toast API interceptor'da zaten gösteriliyor, burada tekrar göstermiyoruz
    } finally {
      setRequestLoading(false);
    }
  };

  useEffect(() => {
    getSiteSets();
  }, [currentDependencySets]);

  useEffect(() => {
    if (selectedSiteSet) {
      getLocations();
    }
  }, [selectedSiteSet]);

  useEffect(() => {
    if (!show) {
      setSelectedName("");
      setSelectedSiteSet(null);
      setLocations([]);
      setSelectedLocations([]);
    }
  }, [show]);

  return (
    <Modal
      title={translate("project_settings.form_dependencies.add_form_dependency_modal.title")}
      positiveButtonNode={translate("save")}
      negativeButtonNode={translate("cancel")}
      positiveButtonHandler={handleAddFormDependency}
      modalSize="lg"
      show={show}
      setShow={(show) => {
        setShow(show);
      }}
    >
      <div className="d-flex flex-wrap align-items-center">
        <div className="col-12 mt-2 pe-3">
          <Input
            type="text"
            name="name"
            value={selectedName}
            title={translate(
              "project_settings.form_dependencies.add_form_dependency_modal.name_label"
            ).toLocaleUpperCase(language)}
            placeholder={translate(
              "project_settings.form_dependencies.add_form_dependency_modal.name_placeholder"
            )}
            onChange={(e) => setSelectedName(e.target.value)}
          />
        </div>
        <div className="col-12 mt-4 pe-3">
          <p className="headTitle text-secondary fw-bold mb-1 ms-1">
            {translate("project_settings.form_dependencies.add_form_dependency_modal.site_set_label")}
          </p>
          {show && (
            <SelectBox
              options={siteSets}
              value={selectedSiteSet}
              placeholder={translate(
                "project_settings.form_dependencies.add_form_dependency_modal.site_set_placeholder"
              )}
              onChange={(value) => {
                setSelectedSiteSet(value)
                setLocationLabels([]);
                setSelectedLocations([]);
              }}
            />
          )}
        </div>
        <div className="col-12 mt-4 pe-3">
          <p className="headTitle text-secondary fw-bold mb-1 ms-1">
            {translate("project_settings.form_dependencies.add_form_dependency_modal.exception_locations_label")}
          </p>
          {show && (
            <>
              <SelectBox
                options={handleDynamicLocations()}
                value={selectedLocations}
                isMulti={true}
                closeMenuOnSelect={true}
                hideSelectedValues={true}
                placeholder={translate(
                  "project_settings.form_dependencies.add_form_dependency_modal.exception_locations_placeholder"
                )}
                onChange={(value) => setSelectedLocations(value)}
              />
              {selectedLocations && selectedLocations.length > 0 && (
                <div className="mt-3 selected-locations-table-container">
                  <table className="selected-locations-table">
                    <thead>
                      <tr>
                        <th>{translate("project_settings.form_dependencies.add_form_dependency_modal.selected_exception_locations")}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedLocations.map((location) => (
                        <tr key={location.value}>
                          <td>{location.label}</td>
                          <td>
                            <button
                              type="button"
                              className="selected-location-remove-btn"
                              onClick={() => {
                                setSelectedLocations(
                                  selectedLocations.filter((loc) => loc.value !== location.value)
                                );
                              }}
                              aria-label="Remove"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddNewFormDependency;
